import React, { useEffect } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { type Dispatch } from "@reduxjs/toolkit";
import { bindAction, type IWizardStep } from "interfaces";

import { type RootState } from "../../../Application/globaltypes/redux";
import { reset, selectEngagementPeopleValue } from "../state/slices/videoPerformanceSlice";
import ReportingExport from "../../../../components/reportingExport/ReportingExport";
import ReportingFilter from "../../../../components/reportingFilter/ReportingFilter";

import VideoPerformanceBody from "./VideoPerformanceBody";
import { selectIsExportActive } from "features/Reporting/state/export/exportSlice";
import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { FeatureFlags } from "featureFlags";

import "./videoPerformance.scss";
import "../../Common/utils/performanceSCSSUtils.scss";
import { usePerformanceFilter } from "features/Library/Common/Hooks/usePerformanceFilter";
import { useAccountFilterShouldShow } from "features/Reporting/Content/queries/useAccountFilterShouldShow";
import { videosStateSelector } from "../state/selectors";

export type Props = PropsFromRedux &
  IWizardStep & {
    videoId: number;
    videoTitle: string;
  };

export type VideoStatusColors = "icon-send-complete" | "icon-send-progress" | "icon-send-canceled";

export const VideoPerformance: React.FC<Props> = ({
  videoId,
  videoTitle,
  resetState,
  acceptHandlers,
  isExportEnabled,
  accountId,
  accountType,
  publisherId,
  associatedFlows,
}) => {
  const accountFilterShouldShow = useAccountFilterShouldShow(accountId, accountType, true);
  const [dateFilter, setDateFilter] = usePerformanceFilter(
    {
      includeAccountsDropdown: accountFilterShouldShow,
      accountId,
      includeDistinct: true,
      supportsSPType: true,
      includeFlows: associatedFlows !== undefined && associatedFlows.length > 0,
    },
    publisherId,
  );

  const reportEnabled = useFeatureFlag(FeatureFlags.VideoReport);

  useEffect(() => {
    return () => {
      resetState();
    };
  }, [resetState]);

  useEffect(() => {
    acceptHandlers?.({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!reportEnabled) {
    return <ReportUnavailable />;
  }

  return (
    <div className="performanceRoot">
      <header className="performanceHeader">
        <h2 className="performanceTitle">Summary</h2>
        <div className="videoActions">
          <ReportingFilter
            currentFilter={dateFilter}
            callback={setDateFilter}
            includeDistinct
            includeAccounts={false}
            includeAccountsDropdown={accountFilterShouldShow}
            supportsSPType
            publisherId={publisherId}
            flowsData={associatedFlows}
          />
          {isExportEnabled && <ReportingExport />}
        </div>
      </header>
      <div className="performanceRoot">
        <VideoPerformanceBody
          includeAccounts={accountFilterShouldShow && (dateFilter.accounts?.length ?? 0) > 0}
          videoId={videoId}
          videoTitle={videoTitle}
          videoFilter={dateFilter}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    peopleDetailsEntities: selectEngagementPeopleValue(state),
    isExportEnabled: selectIsExportActive(state),
    accountId: state.userProfile.accountId,
    accountType: state.userProfile.accountTypeId,
    publisherId: videosStateSelector(state).base.videoInfoReducer.videoInfo.publisherId,
    associatedFlows: videosStateSelector(state).base.videoInfoReducer.videoInfo.flows,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  resetState: bindAction(reset, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(VideoPerformance);
export default ConnectedComponent;
